import * as React from "react"

import { cn } from "@/shared/ui/cn"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/primitives/select"
import { Input } from "@/shared/ui/primitives/input"
import {
  loadPhoneNumberLib,
  type CountryCode,
  type PhoneNumberLib,
} from "@/shared/ui/primitives/load-phone-number-lib"

type Country = {
  code: string
  name: string
  dialCode: string
  flag: string
}

const COUNTRIES: readonly Country[] = [
  { code: "US", name: "United States", dialCode: "+1", flag: "🇺🇸" },
  { code: "CA", name: "Canada", dialCode: "+1", flag: "🇨🇦" },
  { code: "BR", name: "Brazil", dialCode: "+55", flag: "🇧🇷" },
  { code: "MX", name: "Mexico", dialCode: "+52", flag: "🇲🇽" },
  { code: "GB", name: "United Kingdom", dialCode: "+44", flag: "🇬🇧" },
  { code: "AU", name: "Australia", dialCode: "+61", flag: "🇦🇺" },
  { code: "DE", name: "Germany", dialCode: "+49", flag: "🇩🇪" },
  { code: "FR", name: "France", dialCode: "+33", flag: "🇫🇷" },
  { code: "IT", name: "Italy", dialCode: "+39", flag: "🇮🇹" },
  { code: "ES", name: "Spain", dialCode: "+34", flag: "🇪🇸" },
  { code: "PT", name: "Portugal", dialCode: "+351", flag: "🇵🇹" },
  { code: "NL", name: "Netherlands", dialCode: "+31", flag: "🇳🇱" },
  { code: "BE", name: "Belgium", dialCode: "+32", flag: "🇧🇪" },
  { code: "CH", name: "Switzerland", dialCode: "+41", flag: "🇨🇭" },
  { code: "AT", name: "Austria", dialCode: "+43", flag: "🇦🇹" },
  { code: "SE", name: "Sweden", dialCode: "+46", flag: "🇸🇪" },
  { code: "NO", name: "Norway", dialCode: "+47", flag: "🇳🇴" },
  { code: "DK", name: "Denmark", dialCode: "+45", flag: "🇩🇰" },
  { code: "FI", name: "Finland", dialCode: "+358", flag: "🇫🇮" },
  { code: "PL", name: "Poland", dialCode: "+48", flag: "🇵🇱" },
  { code: "RU", name: "Russia", dialCode: "+7", flag: "🇷🇺" },
  { code: "CN", name: "China", dialCode: "+86", flag: "🇨🇳" },
  { code: "JP", name: "Japan", dialCode: "+81", flag: "🇯🇵" },
  { code: "KR", name: "South Korea", dialCode: "+82", flag: "🇰🇷" },
  { code: "IN", name: "India", dialCode: "+91", flag: "🇮🇳" },
  { code: "AR", name: "Argentina", dialCode: "+54", flag: "🇦🇷" },
  { code: "CL", name: "Chile", dialCode: "+56", flag: "🇨🇱" },
  { code: "CO", name: "Colombia", dialCode: "+57", flag: "🇨🇴" },
  { code: "PE", name: "Peru", dialCode: "+51", flag: "🇵🇪" },
  { code: "VE", name: "Venezuela", dialCode: "+58", flag: "🇻🇪" },
  { code: "ZA", name: "South Africa", dialCode: "+27", flag: "🇿🇦" },
  { code: "EG", name: "Egypt", dialCode: "+20", flag: "🇪🇬" },
  { code: "NG", name: "Nigeria", dialCode: "+234", flag: "🇳🇬" },
  { code: "KE", name: "Kenya", dialCode: "+254", flag: "🇰🇪" },
  { code: "NZ", name: "New Zealand", dialCode: "+64", flag: "🇳🇿" },
  { code: "SG", name: "Singapore", dialCode: "+65", flag: "🇸🇬" },
  { code: "MY", name: "Malaysia", dialCode: "+60", flag: "🇲🇾" },
  { code: "TH", name: "Thailand", dialCode: "+66", flag: "🇹🇭" },
  { code: "PH", name: "Philippines", dialCode: "+63", flag: "🇵🇭" },
  { code: "ID", name: "Indonesia", dialCode: "+62", flag: "🇮🇩" },
  { code: "VN", name: "Vietnam", dialCode: "+84", flag: "🇻🇳" },
  { code: "AE", name: "United Arab Emirates", dialCode: "+971", flag: "🇦🇪" },
  { code: "SA", name: "Saudi Arabia", dialCode: "+966", flag: "🇸🇦" },
  { code: "IL", name: "Israel", dialCode: "+972", flag: "🇮🇱" },
  { code: "TR", name: "Turkey", dialCode: "+90", flag: "🇹🇷" },
  { code: "GR", name: "Greece", dialCode: "+30", flag: "🇬🇷" },
  { code: "IE", name: "Ireland", dialCode: "+353", flag: "🇮🇪" },
  { code: "CZ", name: "Czech Republic", dialCode: "+420", flag: "🇨🇿" },
  { code: "HU", name: "Hungary", dialCode: "+36", flag: "🇭🇺" },
  { code: "RO", name: "Romania", dialCode: "+40", flag: "🇷🇴" },
  { code: "BG", name: "Bulgaria", dialCode: "+359", flag: "🇧🇬" },
  { code: "HR", name: "Croatia", dialCode: "+385", flag: "🇭🇷" },
  { code: "SK", name: "Slovakia", dialCode: "+421", flag: "🇸🇰" },
  { code: "SI", name: "Slovenia", dialCode: "+386", flag: "🇸🇮" },
  { code: "EE", name: "Estonia", dialCode: "+372", flag: "🇪🇪" },
  { code: "LV", name: "Latvia", dialCode: "+371", flag: "🇱🇻" },
  { code: "LT", name: "Lithuania", dialCode: "+370", flag: "🇱🇹" },
] as const

function findInitialCountry(value: string): Country {
  if (!value.startsWith("+")) {
    return COUNTRIES[0]
  }
  const sorted = [...COUNTRIES].sort(
    (a, b) => b.dialCode.length - a.dialCode.length
  )
  const match = sorted.find((c) => value.startsWith(c.dialCode))
  return match ?? COUNTRIES[0]
}

function initialDigits(value: string, country: Country): string {
  if (value.startsWith(country.dialCode)) {
    return value.slice(country.dialCode.length)
  }
  if (value.startsWith("+")) {
    return value.slice(1)
  }
  return value
}

function emitValue(country: Country, digits: string): string {
  if (digits === "") return ""
  return country.dialCode + digits
}

function formatNationalNumber(
  lib: PhoneNumberLib | null,
  country: Country,
  digits: string
): string {
  if (digits === "") return ""
  if (!lib) return digits
  return new lib.AsYouType(country.code as CountryCode).input(digits)
}

export type PhoneInputProps = {
  value: string
  onChange: (e164: string) => void
  onBlur?: () => void
  disabled?: boolean
  className?: string
  id?: string
  autoFocus?: boolean
  placeholder?: string
  autoComplete?: string
}

function PhoneInput({
  value,
  onChange,
  onBlur,
  disabled,
  className,
  id,
  autoFocus,
  placeholder,
  autoComplete,
}: PhoneInputProps) {
  const [country, setCountry] = React.useState<Country>(() =>
    findInitialCountry(value)
  )
  const [digits, setDigits] = React.useState<string>(() =>
    initialDigits(value, findInitialCountry(value))
  )
  const [lib, setLib] = React.useState<PhoneNumberLib | null>(null)
  const [syncedValue, setSyncedValue] = React.useState<string>(value)

  if (value !== syncedValue) {
    setSyncedValue(value)
    if (value !== emitValue(country, digits)) {
      const nextCountry = findInitialCountry(value)
      setCountry(nextCountry)
      setDigits(initialDigits(value, nextCountry))
    }
  }

  React.useEffect(() => {
    let cancelled = false
    loadPhoneNumberLib()
      .then((loaded) => {
        if (!cancelled) setLib(loaded)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const handleCountryChange = (code: string) => {
    const next = COUNTRIES.find((c) => c.code === code) ?? COUNTRIES[0]
    setCountry(next)
    onChange(emitValue(next, digits))
  }

  const handleDigitsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    const cleaned = raw.replace(/\D/g, "")
    const deletedFormattingChar =
      raw.length < formatNationalNumber(lib, country, digits).length &&
      cleaned.length === digits.length
    const candidate = deletedFormattingChar ? cleaned.slice(0, -1) : cleaned
    const tooLong =
      lib !== null &&
      candidate.length > digits.length &&
      lib.validatePhoneNumberLength(
        emitValue(country, candidate),
        country.code as CountryCode
      ) === "TOO_LONG"
    const next = tooLong ? digits : candidate
    setDigits(next)
    onChange(emitValue(country, next))
  }

  return (
    <div className={cn("flex gap-2", className)}>
      <Select
        value={country.code}
        onValueChange={handleCountryChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-28">
          <SelectValue>
            {country.flag} {country.dialCode}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {COUNTRIES.map((c) => (
            <SelectItem key={c.code} value={c.code}>
              {c.flag} {c.name} ({c.dialCode})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete={autoComplete ?? "tel"}
        autoFocus={autoFocus}
        placeholder={placeholder}
        disabled={disabled}
        value={formatNationalNumber(lib, country, digits)}
        onChange={handleDigitsChange}
        onBlur={onBlur}
      />
    </div>
  )
}

export { PhoneInput }
